import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonIcon } from '@resideo/blueprint-oneportal';
import { SubHeading, Divider, StyledSelect, LabelTitle } from '../shared/primitives';
import styles from './ActiveDeterrenceEditModal.module.css';
import { chevronLeftIcon } from '@/assets/icons';

interface ActiveDeterrenceEditProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: DeterrenceSettings) => void;
  initialSettings?: DeterrenceSettings;
}

export interface DeterrenceSettings {
  lightStyle: string;
  soundEffect: string;
  soundVolume: number;
  timeout: string;
}


const ActiveDeterrenceEdit: React.FC<ActiveDeterrenceEditProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSettings,
}) => {
  const { t } = useTranslation();

  // Translation helpers
  const getLightStyleLabel = (value: string) => t(`bulkEditSettings.deterrence.lightStyles.${value}`, value);
  const getSoundEffectLabel = (value: string) => t(`bulkEditSettings.deterrence.soundEffects.${value}`, value);
  const getTimeoutLabel = (value: string) => t(`bulkEditSettings.deterrence.timeoutOptions.${value}`, value);
  const getBehaviorNameLabel = (value: string) => t(`bulkEditSettings.deterrence.behaviorNames.${value}`, value);

  // Derive option keys from locale objects so there's a single source of truth
  const lightStylesObjRaw = t('bulkEditSettings.deterrence.lightStyles', { returnObjects: true }) as unknown;
  const soundEffectsObjRaw = t('bulkEditSettings.deterrence.soundEffects', { returnObjects: true }) as unknown;
  const timeoutOptionsObjRaw = t('bulkEditSettings.deterrence.timeoutOptions', { returnObjects: true }) as unknown;

  // If i18n returns objects, read keys from them (preserving JSON insertion order).
  // If not (e.g. in tests where `t` is mocked to return strings), fall back to sensible defaults.
  let LIGHT_STYLE_VALUES: string[];
  if (typeof lightStylesObjRaw === 'object' && lightStylesObjRaw !== null) {
    LIGHT_STYLE_VALUES = Object.keys(lightStylesObjRaw as Record<string, string> ).filter(k => k !== 'None');
  } else {
    LIGHT_STYLE_VALUES = ['ColorStrobe', 'RedStrobe', 'WhiteStrobe', 'WhiteSpotlight'];
  }

  let SOUND_EFFECT_VALUES: string[];
  if (typeof soundEffectsObjRaw === 'object' && soundEffectsObjRaw !== null) {
    SOUND_EFFECT_VALUES = Object.keys(soundEffectsObjRaw as Record<string, string> );
    if (SOUND_EFFECT_VALUES.includes('Disable')) {
      SOUND_EFFECT_VALUES = ['Disable', ...SOUND_EFFECT_VALUES.filter(k => k !== 'Disable')];
    }
  } else {
    SOUND_EFFECT_VALUES = ['Disable', 'Siren', 'Ping'];
  }

  let TIMEOUT_VALUES: string[];
  if (typeof timeoutOptionsObjRaw === 'object' && timeoutOptionsObjRaw !== null) {
    TIMEOUT_VALUES = Object.keys(timeoutOptionsObjRaw as Record<string, string> );
    if (TIMEOUT_VALUES.includes('30s')) {
      TIMEOUT_VALUES = ['30s', ...TIMEOUT_VALUES.filter(k => k !== '30s')];
    }
  } else {
    TIMEOUT_VALUES = ['30s', '10s', '20s', '60s', '120s'];
  }

  const [lightStyle, setLightStyle] = useState(initialSettings?.lightStyle ?? (LIGHT_STYLE_VALUES[0] ?? 'ColorStrobe'));
  const [soundEffect, setSoundEffect] = useState(initialSettings?.soundEffect ?? (SOUND_EFFECT_VALUES[0] ?? 'Disable'));
  const [soundVolume, setSoundVolume] = useState(initialSettings?.soundVolume ?? 10);
  const [timeout, setTimeoutSeconds] = useState(initialSettings?.timeout ?? (TIMEOUT_VALUES[0] ?? '30s'));

  if (!isOpen) return null;

  const handleSave = () => {
    onSave({ lightStyle, soundEffect, soundVolume, timeout });
    onClose();
  };

  return (
    <div className={styles.editPage}>
      {/* Header with back button */}
      <div className={styles.editHeader}>
        <ButtonIcon size="small" iconType="icon" aria-label={t('common.back')} onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ButtonIcon>
        <h2 className={styles.editTitle}>{t('bulkEditSettings.deterrence.edit.title')}</h2>
      </div>

      <div className={styles.editContent}>
        {/* Behavior Name */}
        <div className={styles.fieldRow}>
          <LabelTitle>{t('bulkEditSettings.deterrence.edit.behaviorName')}</LabelTitle>
          <input type="text" className={styles.textInput} value={getBehaviorNameLabel('Default')} readOnly />
        </div>

        {/* Light Settings */}
        <SubHeading className={styles.sectionTitle}>{t('bulkEditSettings.deterrence.edit.lightSettings')}</SubHeading>
        <Divider />
        
        <LabelTitle className={styles.fieldLabel}>{t('bulkEditSettings.deterrence.edit.ledBehavior')}</LabelTitle>
        <div className={styles.radioGroup}>
          {LIGHT_STYLE_VALUES.map(value => (
            <label key={value} className={styles.radioLabel}>
              <input
                type="radio"
                name="lightStyle"
                value={value}
                checked={lightStyle === value}
                onChange={e => setLightStyle(e.target.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioText}>{getLightStyleLabel(value)}</span>
            </label>
          ))}
        </div>

        {/* Sound Settings */}
        <SubHeading className={styles.sectionTitle}>{t('bulkEditSettings.deterrence.edit.soundSettings')}</SubHeading>
        <Divider />
        
        <LabelTitle className={styles.fieldLabel}>{t('bulkEditSettings.deterrence.edit.soundEffect')}</LabelTitle>
        <div className={styles.radioGroup}>
          {SOUND_EFFECT_VALUES.map(value => (
            <label key={value} className={styles.radioLabel}>
              <input
                type="radio"
                name="soundEffect"
                value={value}
                checked={soundEffect === value}
                onChange={e => setSoundEffect(e.target.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioText}>{getSoundEffectLabel(value)}</span>
              {value !== 'Disable' && (
                <button
                  type="button"
                  className={styles.listenLink}
                  onClick={e => e.stopPropagation()}
                >
                  
                  <svg className={styles.listenIcon} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M8 3v10M5 6v4M11 5v6M2 7v2M14 7v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  {t('bulkEditSettings.deterrence.edit.listen')}
                </button>
              )}
            </label>
          ))}
        </div>

        <LabelTitle className={styles.volumeLabel}>{t('bulkEditSettings.deterrence.edit.soundVolume')}</LabelTitle>
        <div className={styles.sliderRow}>
          <span className={styles.sliderValue}>{soundVolume}</span>
          <input
            type="range"
            min={0}
            max={10}
            value={soundVolume}
            onChange={e => setSoundVolume(Number(e.target.value))}
            className={styles.slider}
          />
        </div>

        {/* Timeout Settings */}
        <SubHeading className={styles.sectionTitle}>{t('bulkEditSettings.deterrence.edit.timeoutSettings')}</SubHeading>
        <Divider />
        
        <LabelTitle className={styles.fieldLabel}>{t('bulkEditSettings.deterrence.edit.timeoutAfterEvent')}</LabelTitle>
        <StyledSelect
          name="timeout"
          value={timeout}
          onChange={e => setTimeoutSeconds(e.target.value)}
          className={styles.timeoutSelect}
        >
          {TIMEOUT_VALUES.map(value => (
            <option key={value} value={value}>{getTimeoutLabel(value)}</option>
          ))}
        </StyledSelect>
      </div>

      <div className={styles.editFooter}>
        <Button variant="text" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={handleSave}>{t('common.save')}</Button>
      </div>
    </div>
  );
};

export default ActiveDeterrenceEdit;
